import { PF, calibriWorkbook } from './tagFont';
import { groupPieces } from './tagGroups';
import ExcelJS from 'exceljs';
import { currentGold, purityPercent } from '@/lib/goldRate';
import { priceTag, rateLabel, lineRate, getPricing } from '@/lib/tagPricing';
import { purityLabel, purityOrder, purityTitle, catOf } from '@/lib/tagSummary';
import { placeSep, xlStyle, styleOfLabel } from '@/lib/tagMatHandling';

// Indian grouping: 1,00,00,000 / 10,00,000 / 10,000 — whole rupees.
const INR_FMT = '[>=10000000]##\\,##\\,##\\,##0;[>=100000]##\\,##\\,##0;##,##0';

const NAVY = 'FF1E2A4A';
const GREEN = 'FF0F6B4F';
const RATE_FILL = 'FFFFF8DC';
const MISSING_FILL = 'FFFFD6D6';
const SOFT = 'FFF3F5F9';
const THIN = { style: 'thin', color: { argb: 'FFD9DEE7' } };
const IMG_PX = 110;
export const ESTIMATE_TITLE = 'Rough Estimation';
export const ESTIMATE_DISCLAIMER = 'The prices (if any) and the weights are indicative and actuals may differ.';

// Heading on top + disclaimer at the bottom of every A4 page of a jsPDF document.
export const estimateTitle = (listName) => (listName ? `${ESTIMATE_TITLE} - List: ${listName}` : ESTIMATE_TITLE);

export function stampEstimatePdf(pdf, listName) {
  const n = pdf.getNumberOfPages();
  for (let i = 1; i <= n; i += 1) {
    pdf.setPage(i);
    const w = pdf.internal.pageSize.getWidth(); const h = pdf.internal.pageSize.getHeight();
    pdf.setFont(PF(), 'bold'); pdf.setFontSize(10); pdf.setTextColor(30, 42, 74);
    pdf.text(pdf.splitTextToSize(estimateTitle(listName), w - 20)[0], w / 2, 5.5, { align: 'center' });
    pdf.setFont(PF(), 'italic'); pdf.setFontSize(7); pdf.setTextColor(91, 100, 117);
    pdf.text(`${ESTIMATE_DISCLAIMER}  ·  Page ${i} of ${n}`, w / 2, h - 3, { align: 'center' });
  }
}
const VIEW_LABEL = { expanded: 'Expanded', collapsed: 'Collapsed', raw: 'Raw' };

// Any image URL → PNG (max 240px) for embedding; null if it can't be read.
export async function imagePng(url) {
  if (!url) return null;
  try {
    const blob = await (await fetch(url)).blob();
    const bmp = await createImageBitmap(blob);
    const k = Math.min(1, 240 / Math.max(bmp.width, bmp.height));
    const c = document.createElement('canvas');
    c.width = Math.round(bmp.width * k); c.height = Math.round(bmp.height * k);
    const ctx = c.getContext('2d');
    ctx.fillStyle = '#ffffff'; ctx.fillRect(0, 0, c.width, c.height);
    ctx.drawImage(bmp, 0, 0, c.width, c.height);
    return { base64: c.toDataURL('image/png'), w: c.width, h: c.height };
  } catch { return null; }
}

function columns(showPieces, withRates) {
  return [
    { key: 'img', header: 'Image', width: 18 },
    { key: 'item', header: 'Jewellery / Raw material', width: 40 },
    { key: 'qty', header: 'Qty', width: 12 },
    { key: 'unit', header: 'Unit', width: 7 },
    ...(showPieces ? [{ key: 'pcs', header: 'Pieces', width: 9 }] : []),
    ...(withRates ? [{ key: 'rate', header: 'Rate', width: 13 }, { key: 'amt', header: 'Amount', width: 16 }, { key: 'sub', header: 'Group total', width: 18 }] : []),
  ];
}

function addBlock(ws, wb, entry, cols, opts, img) {
  const { data, materials } = entry;
  const L = Object.fromEntries(cols.map((c, i) => [c.key, String.fromCharCode(65 + i)]));
  const start = ws.rowCount + 1;
  const pur = purityLabel(data);
  // Hidden helper cells (category + purity) let the summary use SUMIFS so it always tallies.
  const mark = (r, cat) => { ws.getCell(`${opts.hc}${r.number}`).value = cat; ws.getCell(`${opts.hp}${r.number}`).value = pur; return r; };
  const put = (vals, style = {}) => {
    const row = ws.addRow(cols.map((c) => vals[c.key] ?? null));
    row.height = 20;
    row.eachCell({ includeEmpty: true }, (cell) => { cell.alignment = { vertical: 'middle' }; Object.assign(cell, style); });
    return row;
  };
  const head = put({ item: data.jewelleryNo });
  head.getCell(L.item).font = { bold: true, size: 12, color: { argb: GREEN } };
  const sub = data.jewelleryType || '';
  if (sub) put({ item: sub }).getCell(L.item).font = { italic: true, color: { argb: 'FF5B6475' } };
  const gross = mark(put({ item: 'Gross weight', qty: Number(data.gross || 0), unit: 'g' }), 'gross');
  gross.getCell(L.qty).numFmt = '0.000';
  gross.getCell(L.item).font = { color: { argb: 'FF5B6475' } };
  const lineRows = [];
  let saleRef = null;
  const line = (vals, fmt, rate, fixed, cat) => {
    const r = mark(put(vals), cat);
    r.getCell(L.qty).numFmt = fmt;
    if (opts.withRates) {
      const rc = r.getCell(L.rate);
      const ac = r.getCell(L.amt);
      rc.numFmt = INR_FMT; ac.numFmt = INR_FMT;
      rc.border = { top: THIN, left: THIN, bottom: THIN, right: THIN };
      if (fixed) {
        // Merged / raw line: amount = sum of its expanded lines, rate = amount ÷ carats.
        rc.value = fixed.rate; ac.value = fixed.amount;
        rc.note = 'Average rate = amount ÷ carats of the merged lines';
      } else {
        const has = rate != null && rate !== '';
        if (has) rc.value = typeof rate === 'object' ? rate : Math.round(Number(rate));
        rc.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: has ? RATE_FILL : MISSING_FILL } };
        ac.value = { formula: `IF(${L.rate}${r.number}="","",ROUND(${L.qty}${r.number}*${L.rate}${r.number},0))` };
      }
    }
    lineRows.push(r.number);
    return r;
  };
  const purity = data.purityText && data.purityText !== '—' ? data.purityText : '';
  const netVals = { item: `Net weight${purity ? ` · ${purity}` : ''}${opts.withRates ? ' (gold rate per g)' : ''}`, qty: Number(Number(data.net || 0).toFixed(3)), unit: 'g' };
  const R = opts.rates || {};
  const pct = opts.gold && !data.purityMissing ? (data.purityPct || purityPercent(purity)) : null;
  // 100% gold rate (typed or portal) converted to the piece's purity wins over the rate card.
  const goldRate = pct ? { formula: `ROUND(${opts.gold.ref}*${pct}/100,0)`, result: Math.round((opts.gold.rate * pct) / 100) } : null;
  const price = opts.withRates ? priceTag(data, opts.rmMode, R, materials).byKey : null;
  const net = line(netVals, '0.000', goldRate, undefined, 'net');
  net.getCell(L.item).font = { bold: true, color: { argb: 'FFB45309' } };
  const netRef = `${L.qty}${net.number}`;
  // Separate weights (e.g. Dak Wt) right after Net weight, in their own colour.
  const sepLine = (sp) => {
    const r = line({ item: sp.label, qty: Number(Number(sp.qty).toFixed(3)), unit: sp.unit }, sp.unit === 'g' ? '0.000' : '0.00', sp.price === 'rate' ? (R.lines || {})[sp.label] ?? null : 0, undefined, 'sep');
    const x = xlStyle(sp.style || styleOfLabel(sp.label));
    r.getCell(L.item).font = { bold: !!x.bold, italic: !!x.italic, ...(x.color ? { color: { argb: x.color } } : {}) };
    if (x.fill) r.getCell(L.item).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: x.fill } };
  };
  placeSep(materials.filter((m) => !m.sepRow), (data.separate || []).map((sp) => ({ ...sp, sepRow: true })), (m) => m.label || rateLabel(m)).forEach((m) => { if (m.sepRow) { sepLine(m); return; } const item = rateLabel(m); line({ item, qty: Number(Number(m.carats || 0).toFixed(3)), unit: 'ct', pcs: m.hidePieces ? null : Number(m.pieces || 0) }, '0.00', price?.[m.key]?.rate ?? lineRate(data, m, R), opts.rmMode !== 'expanded' || m.parts > 1 ? price?.[m.key] : undefined, catOf(m)); });
  if (opts.withRates) {
    line({ item: 'Labour - per g of net wt', qty: { formula: netRef }, unit: 'g' }, '0.000', R.labour, undefined, 'labour');
    // Extra charges: one line each after Labour, amount only (counted in the sale value).
    (data.charges || []).forEach((c) => {
      const r = mark(put({ item: `${c.name} (extra charge)` }), 'charge');
      const ac = r.getCell(L.amt);
      if (c.amount != null && c.amount !== '') ac.value = Math.round(Number(c.amount));
      ac.numFmt = INR_FMT;
      lineRows.push(r.number);
    });
    const sale = put({ item: 'Sale value' });
    saleRef = `${L.amt}${sale.number}`;
    sale.getCell(L.amt).value = { formula: `SUM(${L.amt}${lineRows[0]}:${L.amt}${lineRows[lineRows.length - 1]})` };
    sale.getCell(L.amt).numFmt = INR_FMT;
    sale.eachCell({ includeEmpty: true }, (cell, ci) => {
      if (ci === 1) return;
      cell.font = { bold: true, color: { argb: NAVY } };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: SOFT } };
    });
    sale.getCell(L.amt).border = { top: { style: 'thin', color: { argb: NAVY } } };
  }
  while (ws.rowCount - start + 1 < 6) put({});
  const end = ws.rowCount;
  ws.mergeCells(`A${start}:A${end}`);
  ws.getRow(end).eachCell({ includeEmpty: true }, (cell) => { cell.border = { ...(cell.border || {}), bottom: { style: 'medium', color: { argb: NAVY } } }; });
  if (img) {
    const id = wb.addImage({ base64: img.base64, extension: 'png' });
    const k = Math.min(IMG_PX / img.w, IMG_PX / img.h);
    ws.addImage(id, { tl: { col: 0.08, row: start - 1 + 0.15 }, ext: { width: img.w * k, height: img.h * k }, editAs: 'oneCell' });
  } else {
    ws.getCell(`A${start}`).value = 'No image';
    ws.getCell(`A${start}`).alignment = { vertical: 'middle', horizontal: 'center' };
    ws.getCell(`A${start}`).font = { color: { argb: 'FF9AA3B2' }, italic: true };
  }
  ws.addRow([]);
  return saleRef;
}

function bandRow(ws, cols, text, value, opts = {}) {
  const r = ws.addRow(cols.map(() => null));
  const last = String.fromCharCode(64 + cols.length);
  const mergeTo = opts.split ? 'B' : String.fromCharCode(64 + cols.length - (value != null ? 1 : 0));
  if (mergeTo !== 'B') ws.mergeCells(`B${r.number}:${mergeTo}${r.number}`);
  r.getCell(2).value = text;
  if (value != null) { r.getCell(cols.length).value = value; r.getCell(cols.length).numFmt = INR_FMT; }
  r.height = opts.big ? 26 : 22;
  for (let c = 1; c <= cols.length; c += 1) {
    const cell = r.getCell(c);
    cell.font = { bold: true, size: opts.big ? 13 : 11, color: { argb: opts.dark ? 'FFFFFFFF' : NAVY } };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: opts.dark ? NAVY : 'FFE6EAF2' } };
    cell.alignment = { vertical: 'middle', horizontal: c === cols.length && value != null ? 'right' : 'left' };
  }
  // Group / grand total amounts (SUM) get a top border.
  if (value?.formula && /^SUM/.test(value.formula)) r.getCell(cols.length).border = { top: { style: 'thin', color: { argb: opts.dark ? 'FFFFFFFF' : NAVY } } };
  return `${last}${r.number}`;
}

// Summary boxes (one per purity + grand total) built from SUMIFS over the hidden helper columns.
function addSummary(ws, cols, entries, opts) {
  const L = Object.fromEntries(cols.map((c, i) => [c.key, String.fromCharCode(65 + i)]));
  const { first, last, hc, hp, withRates, values, kind } = opts;
  const V = withRates ? L.amt : String.fromCharCode(65 + cols.length);
  const rng = (col) => `$${col}$${first}:$${col}$${last}`;
  const crit = (cat, pur) => `${rng(hc)},"${cat}"${pur ? `,${rng(hp)},"${pur.replace(/"/g, '""')}"` : ''}`;
  const sumif = (col, cat, pur) => ({ formula: `SUMIFS(${rng(col)},${crit(cat, pur)})` });
  const style = (r, bold, fillArgb) => r.eachCell({ includeEmpty: true }, (c, ci) => {
    if (ci === 1) return;
    c.font = { bold: !!bold, color: { argb: NAVY } };
    if (fillArgb) c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: fillArgb } };
    c.border = { bottom: THIN };
  });
  const box = (title, pur, count, dark) => {
    const h = ws.addRow([]);
    h.getCell(2).value = `${title} · ${count} piece${count === 1 ? '' : 's'}`;
    if (values !== 'none') { h.getCell(V).value = 'Value'; h.getCell(V).alignment = { horizontal: 'right' }; }
    h.eachCell({ includeEmpty: true }, (c, ci) => { if (ci === 1) return; c.font = { bold: true, color: { argb: 'FFFFFFFF' } }; c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: dark ? GREEN : NAVY } }; });
    for (let ci = 2; ci <= Math.max(cols.length, V.charCodeAt(0) - 64); ci += 1) { const c = h.getCell(ci); c.font = { bold: true, color: { argb: 'FFFFFFFF' } }; c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: dark ? GREEN : NAVY } }; }
    h.height = 22;
    const vals = [];
    const row = (label, qty, unit, cat, pcs, bold) => {
      const r = ws.addRow([]);
      r.getCell(2).value = label;
      if (qty) { r.getCell(L.qty).value = qty; r.getCell(L.qty).numFmt = unit === 'ct' ? '0.00' : '0.000'; r.getCell(L.unit).value = unit; }
      if (pcs && L.pcs) r.getCell(L.pcs).value = sumif(L.pcs, cat, pur);
      if (values !== 'none' && cat) {
        const c = r.getCell(V);
        if (values === 'show') { c.value = sumif(L.amt, cat, pur); c.numFmt = INR_FMT; vals.push(`${V}${r.number}`); } else { c.value = '-'; c.alignment = { horizontal: 'right' }; }
      }
      style(r, bold);
      return r;
    };
    row('Gross weight', sumif(L.qty, 'gross', pur), 'g', null);
    row('Net weight (gold value)', sumif(L.qty, 'net', pur), 'g', 'net');
    (kind === 'weights' ? [] : [['dia', 'Diamonds'], ['stone', 'Colour stones'], ['pearl', 'Pearls'], ['other', 'Other materials']]).forEach(([k, label]) => {
      if (k === 'other' && !entries.some((e) => (!pur || purityLabel(e.data) === pur) && e.materials.some((m) => catOf(m) === 'other'))) return;
      row(label, sumif(L.qty, k, pur), 'ct', k, true);
    });
    if (values !== 'none') {
      if (kind !== 'weights') { row('Labour', null, null, 'labour'); row('Other charges', null, null, 'charge'); }
      const t = ws.addRow([]);
      t.getCell(2).value = 'Total value';
      t.getCell(V).value = values === 'show' ? (kind === 'weights' ? { formula: `SUMIFS(${rng(L.amt)},${rng(hc)},"<>"${pur ? `,${rng(hp)},"${pur.replace(/"/g, '""')}"` : ''})` } : { formula: `SUM(${vals.join(',')})` }) : '-';
      t.getCell(V).numFmt = INR_FMT; t.getCell(V).alignment = { horizontal: 'right' };
      style(t, true, 'FFFEF3C7');
    }
    ws.addRow([]);
  };
  ws.addRow([]);
  const sh = ws.addRow([]);
  sh.getCell(2).value = 'Summary';
  sh.getCell(2).font = { bold: true, size: 14, color: { argb: NAVY } };
  const purs = purityOrder(entries.map((e) => e.data));
  purs.forEach((p) => box(purityTitle(p) + (purs.length > 1 ? '' : ' · list total'), p, entries.filter((e) => purityLabel(e.data) === p).length, false));
  if (purs.length > 1) box('Grand total', null, entries.length, true);
}


const pageGroups = (entries) => groupPieces(entries, (e) => e.data.pageNo, (e) => e.data.jewelleryNo, (e) => e.data.jewelleryType);

// entries: [{ data, materials }] exactly as shown on the tag.
export async function downloadTagsExcel(entries, { rmMode = 'expanded', showPieces = true, withRates = true, fileName, rates, listName, shared = false, summaryKind = 'full' } = {}) {
  const wb = new ExcelJS.Workbook();
  wb.creator = 'Jewellery Tags';
  const ws = wb.addWorksheet('Tags', { views: [{ state: 'frozen', ySplit: 4 }] });
  const cols = columns(showPieces, withRates);
  ws.columns = cols.map((c) => ({ key: c.key, width: c.width }));
  const last = String.fromCharCode(64 + cols.length);
  ws.mergeCells(`A1:${last}1`);
  ws.getCell('A1').value = estimateTitle(listName);
  ws.getCell('A1').font = { bold: true, size: 15, color: { argb: NAVY } };
  ws.getRow(1).height = 26;
  ws.mergeCells(`A2:${last}2`);
  ws.getCell('A2').value = `${entries.length} piece${entries.length === 1 ? '' : 's'} · ${VIEW_LABEL[rmMode] || rmMode} view · ${showPieces ? 'with' : 'without'} pieces · ${new Date().toLocaleDateString('en-GB')}${withRates ? ` · ${rates?.name ? `rates from "${rates.name}" · ` : ''}rates and amounts in whole rupees (gold and labour are per gram of net weight)` : ''}`;
  ws.getCell('A2').font = { size: 10, color: { argb: 'FF5B6475' } };
  const g = withRates ? currentGold() : null;
  const gold = g ? { ...g, ref: '$C$3' } : null;
  if (gold) {
    ws.getCell('B3').value = `Gold rate 100% per g (${g.source}) — net weight rate = this × purity %`;
    ws.getCell('B3').font = { bold: true, color: { argb: 'FFB45309' } };
    const c = ws.getCell('C3');
    c.value = Math.round(g.rate); c.numFmt = INR_FMT; c.font = { bold: true };
    c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: RATE_FILL } };
    c.border = { top: THIN, left: THIN, bottom: THIN, right: THIN };
    ws.getRow(3).height = 20;
  }
  const hr = ws.getRow(4);
  cols.forEach((c, i) => {
    const cell = hr.getCell(i + 1);
    cell.value = c.header;
    cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: NAVY } };
    cell.alignment = { vertical: 'middle', horizontal: i > 1 ? 'right' : 'left' };
  });
  hr.height = 22;
  ws.headerFooter = { oddHeader: `&C&"-,Bold"&14${estimateTitle(listName).replace(/&/g, '&&')}`, oddFooter: `&C&8${ESTIMATE_DISCLAIMER}  ·  Page &P of &N` };
  ws.pageSetup = { paperSize: 9, orientation: 'portrait', fitToPage: true, fitToWidth: 1, fitToHeight: 0, printTitlesRow: '4:4' };
  const imgs = await Promise.all(entries.map((e) => imagePng(e.data.image)));
  const imgOf = new Map(entries.map((e, i) => [e, imgs[i]]));
  const subs = [];
  const hc = String.fromCharCode(65 + cols.length + 2); const hp = String.fromCharCode(65 + cols.length + 3);
  ws.getColumn(hc).hidden = true; ws.getColumn(hp).hidden = true;
  const first = ws.rowCount + 1;
  pageGroups(entries).forEach((g) => {
    bandRow(ws, cols, g.label, null);
    ws.addRow([]);
    const gStart = ws.rowCount + 1;
    const sales = g.items.map((e) => addBlock(ws, wb, e, cols, { withRates, rates, gold, rmMode, hc, hp }, imgOf.get(e))).filter(Boolean);
    const gEnd = ws.rowCount;
    // Group total: gross weight under Qty, and the group's amount in the last column when rates are shown.
    const gt = bandRow(ws, cols, `${g.label} · total (${g.items.length} piece${g.items.length === 1 ? '' : 's'})`, withRates ? { formula: `SUM(${sales.join(',')})` } : null, { split: 3 });
    const gr = ws.getRow(Number(gt.replace(/^[A-Z]+/, '')));
    gr.getCell(3).value = { formula: `SUMIFS(C${gStart}:C${gEnd},${hc}${gStart}:${hc}${gEnd},"gross")`, result: g.items.reduce((t, e) => t + Number(e.data.gross || 0), 0) }; gr.getCell(3).numFmt = '0.000'; gr.getCell(3).alignment = { horizontal: 'right', vertical: 'middle' };
    gr.getCell(4).value = 'g';
    if (withRates) subs.push(gt);
    ws.addRow([]);
  });
  const lastData = ws.rowCount;
  const gtc = bandRow(ws, cols, `Grand total (${entries.length} piece${entries.length === 1 ? '' : 's'})`, withRates ? { formula: `SUM(${subs.join(',')})` } : null, { dark: true, big: true, split: 3 });
  const gtr = ws.getRow(Number(gtc.replace(/^[A-Z]+/, '')));
  gtr.getCell(3).value = { formula: `SUMIFS(C${first}:C${lastData},${hc}${first}:${hc}${lastData},"gross")`, result: entries.reduce((t, e) => t + Number(e.data.gross || 0), 0) }; gtr.getCell(3).numFmt = '0.000'; gtr.getCell(3).alignment = { horizontal: 'right', vertical: 'middle' };
  gtr.getCell(4).value = 'g';
  if (entries.length && summaryKind !== 'none') addSummary(ws, cols, entries, { kind: summaryKind, first, last: lastData, hc, hp, withRates, values: withRates ? 'show' : getPricing().canView && !shared ? 'dash' : 'none' });
  ws.addRow([]);
  const dr = ws.addRow([ESTIMATE_DISCLAIMER]);
  ws.mergeCells(`A${dr.number}:${last}${dr.number}`);
  dr.getCell(1).font = { italic: true, size: 9, color: { argb: 'FF5B6475' } };
  const buf = await calibriWorkbook(wb).xlsx.writeBuffer();
  const url = URL.createObjectURL(new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName || `Jewellery_Tags_${new Date().toISOString().slice(0, 10)}.xlsx`;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}

const mode = (vals) => {
  const c = new Map();
  vals.forEach((v) => c.set(v, (c.get(v) || 0) + 1));
  return [...c.entries()].sort((a, b) => b[1] - a[1])[0]?.[0];
};
const summarise = (vals) => {
  const c = new Map();
  vals.forEach((v) => c.set(v, (c.get(v) || 0) + 1));
  return [...c.entries()].map(([v, n]) => ({ v, n })).sort((a, b) => b.n - a.n);
};

// Read rates typed into an Excel exported by the portal.
// → { lines: {label: {rate, seen}}, gold: {purity: {rate, seen}}, labour: {rate, seen}|null, missing: [labels] }
export async function parseRatesWorkbook(file) {
  const XLSX = await import('xlsx');
  const wb = XLSX.read(await file.arrayBuffer(), { type: 'array' });
  const rows = XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]], { header: 1, raw: true, defval: null });
  const head = (rows[3] || []).map((v) => String(v ?? '').trim());
  const rateCol = head.indexOf('Rate');
  const unitCol = head.indexOf('Unit');
  if (rateCol < 0) throw new Error('This file has no Rate column. Download an Excel with rate columns from the portal first.');
  const acc = { lines: {}, gold: {}, labour: [] };
  const missing = new Set();
  const num = (x) => (x === '' || x == null || Number.isNaN(Number(x)) ? null : Number(x));
  rows.slice(4).forEach((row) => {
    const item = String(row[1] ?? '').trim();
    if (!item) return;
    const rate = num(row[rateCol]);
    const unit = String(row[unitCol] ?? '').trim();
    let bucket = null; let key = item;
    const g = item.match(/^Net weight · (.+?) \(gold rate per g\)$/);
    if (g) return; // gold comes from the 100% gold rate, not the rate card
    if (/^Labour\b/.test(item)) { bucket = 'labour'; } else if (unit === 'ct') { bucket = acc.lines; }
    if (!bucket) return;
    if (rate == null) { missing.add(bucket === 'labour' ? 'Labour' : bucket === acc.gold ? `Gold ${key}` : key); return; }
    if (bucket === 'labour') acc.labour.push(rate); else (bucket[key] = bucket[key] || []).push(rate);
  });
  const pack = (vals) => ({ rate: mode(vals), seen: summarise(vals) });
  const lines = Object.fromEntries(Object.entries(acc.lines).map(([k, v]) => [k, pack(v)]));
  const gold = Object.fromEntries(Object.entries(acc.gold).map(([k, v]) => [k, pack(v)]));
  const filled = new Set([...Object.keys(lines), ...Object.keys(gold).map((k) => `Gold ${k}`), ...(acc.labour.length ? ['Labour'] : [])]);
  return { lines, gold, labour: acc.labour.length ? pack(acc.labour) : null, missing: [...missing].filter((m) => !filled.has(m)).sort() };
}

// Blank (or pre-filled) rate-card template: every unique expanded line on record + gold purities + labour.
export async function downloadRateTemplate({ lines, card, title, groupOf }) {
  const wb = new ExcelJS.Workbook();
  const ws = wb.addWorksheet('Rates', { views: [{ state: 'frozen', ySplit: 4 }] });
  ws.columns = [{ width: 14 }, { width: 46 }, { width: 8 }, { width: 14 }];
  ws.mergeCells('A1:D1');
  ws.getCell('A1').value = `Rate card template${title ? ` · ${title}` : ''}`;
  ws.getCell('A1').font = { bold: true, size: 14, color: { argb: NAVY } };
  ws.mergeCells('A2:D2');
  ws.getCell('A2').value = 'Fill the yellow Rate column in whole rupees (raw material per ct; labour per g of net weight). Gold is not on the rate card — it is taken from the 100% gold rate chosen at download time., then upload it in Customer rate cards → Import rates from Excel.';
  ws.getCell('A2').font = { size: 10, color: { argb: 'FF5B6475' } };
  const hr = ws.getRow(4);
  ['Type', 'Item', 'Unit', 'Rate'].forEach((h, i) => {
    const c = hr.getCell(i + 1);
    c.value = h; c.font = { bold: true, color: { argb: 'FFFFFFFF' } };
    c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: NAVY } };
  });
  const add = (type, item, unit, rate) => {
    const r = ws.addRow([type, item, unit, rate == null ? null : Math.round(rate)]);
    const c = r.getCell(4);
    c.numFmt = INR_FMT;
    c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: RATE_FILL } };
    c.border = { top: THIN, left: THIN, bottom: THIN, right: THIN };
  };
  add('Labour', 'Labour - per g of net wt', 'g', card?.labour);
  lines.forEach((l) => add(groupOf ? groupOf(l) : 'Raw material', l, 'ct', card?.lines?.[l]));
  const buf = await calibriWorkbook(wb).xlsx.writeBuffer();
  const url = URL.createObjectURL(new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
  const a = document.createElement('a');
  a.href = url; a.download = `Rate_card_template${card?.name ? `_${card.name}` : ''}.xlsx`;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}
